import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { INSTALLATION_PHASES } from "@/utils/installationPhases";

export interface EstimateLineItem {
  id?: string;
  estimate_id: string;
  product_id: string | null;
  description: string;
  quantity: number;
  unit: string;
  unit_price: number;
  discount_percent: number;
  discount_amount: number;
  line_total: number;
  sort_order?: number;
  calculation_source?: 'auto' | 'manual' | 'edited';
  calculation_note?: string | null;
  created_at?: string;
  products?: {
    install_phase: string | null;
    tier?: string | null;
    name?: string;
    brand?: string;
  };
}

// Map phase keys to phase numbers for sorting
const getPhaseNumber = (phaseKey: string | null): number => {
  if (!phaseKey) return 999; // Items without phase go to end
  
  const phase = INSTALLATION_PHASES.find(p => p.key === phaseKey);
  return phase?.number ?? 999;
};

export const useEstimateLineItems = (estimateId: string | undefined) => {
  const queryClient = useQueryClient();

  const { data: lineItems = [], isLoading } = useQuery({
    queryKey: ['estimate-line-items', estimateId],
    queryFn: async () => {
      if (!estimateId) return [];
      
      const { data, error } = await supabase
        .from('estimate_line_items')
        .select(`
          *,
          products!estimate_line_items_product_id_fkey (
            install_phase
          )
        `)
        .eq('estimate_id', estimateId);

      if (error) throw error;
      
      // Sort by installation phase, then by sort_order within phase
      const sorted = (data || []).sort((a, b) => {
        const phaseA = getPhaseNumber(a.products?.install_phase);
        const phaseB = getPhaseNumber(b.products?.install_phase);
        
        if (phaseA !== phaseB) {
          return phaseA - phaseB; // Sort by phase number
        }
        
        // Within same phase, sort by selection order
        return (a.sort_order || 0) - (b.sort_order || 0);
      });
      
      return sorted as EstimateLineItem[];
    },
    enabled: !!estimateId,
  });

  const addLineItem = useMutation({
    mutationFn: async (lineItem: EstimateLineItem) => {
      const maxOrder = lineItems.length > 0 
        ? Math.max(...lineItems.map(item => item.sort_order || 0))
        : -1;

      const { data, error } = await supabase
        .from('estimate_line_items')
        .insert({
          ...lineItem,
          sort_order: maxOrder + 1,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['estimate-line-items', estimateId] });
      queryClient.invalidateQueries({ queryKey: ['estimates'] });
      toast.success("Line item added");
    },
    onError: (error: any) => {
      toast.error("Failed to add line item: " + error.message);
    },
  });

  const addMultipleLineItems = useMutation({
    mutationFn: async (lineItems: EstimateLineItem[]) => {
      const maxOrder = lineItems.length > 0 
        ? Math.max(...lineItems.map(item => item.sort_order || 0))
        : -1;

      const itemsWithOrder = lineItems.map((item, index) => ({
        ...item,
        sort_order: maxOrder + index + 1,
      }));

      const { data, error } = await supabase
        .from('estimate_line_items')
        .insert(itemsWithOrder)
        .select();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['estimate-line-items', estimateId] });
      queryClient.invalidateQueries({ queryKey: ['estimates'] });
      toast.success(`${data.length} line items added`);
    },
    onError: (error: any) => {
      toast.error("Failed to add line items: " + error.message);
    },
  });

  const updateLineItem = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<EstimateLineItem> }) => {
      const { data, error } = await supabase
        .from('estimate_line_items')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['estimate-line-items', estimateId] });
      queryClient.invalidateQueries({ queryKey: ['estimates'] });
    },
    onError: (error: any) => {
      toast.error("Failed to update line item: " + error.message);
    },
  });

  const deleteLineItem = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('estimate_line_items')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['estimate-line-items', estimateId] });
      queryClient.invalidateQueries({ queryKey: ['estimates'] });
      toast.success("Line item removed");
    },
    onError: (error: any) => {
      toast.error("Failed to delete line item: " + error.message);
    },
  });

  const reorderLineItems = useMutation({
    mutationFn: async (reorderedItems: { id: string; sort_order: number }[]) => {
      const updates = reorderedItems.map(item =>
        supabase
          .from('estimate_line_items')
          .update({ sort_order: item.sort_order })
          .eq('id', item.id)
      );

      await Promise.all(updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['estimate-line-items', estimateId] });
    },
    onError: (error: any) => {
      toast.error("Failed to reorder line items: " + error.message);
    },
  });

  const replaceOrAddLineItem = useMutation({
    mutationFn: async ({ 
      lineItem, 
      existingItemId 
    }: { 
      lineItem: EstimateLineItem; 
      existingItemId?: string 
    }) => {
      if (existingItemId) {
        // UPDATE existing item - keep quantity, update product details
        const { data, error } = await supabase
          .from('estimate_line_items')
          .update({
            product_id: lineItem.product_id,
            description: lineItem.description,
            unit: lineItem.unit,
            unit_price: lineItem.unit_price,
            line_total: lineItem.line_total,
            calculation_source: 'edited',
            calculation_note: lineItem.calculation_note 
              ? `${lineItem.calculation_note} (Updated manually)`
              : 'Updated manually',
          })
          .eq('id', existingItemId)
          .select()
          .single();
        
        if (error) throw error;
        return { data, action: 'replaced' as const };
      } else {
        // INSERT new item
        const maxOrder = lineItems.length > 0 
          ? Math.max(...lineItems.map(item => item.sort_order || 0))
          : -1;
        
        const { data, error } = await supabase
          .from('estimate_line_items')
          .insert({
            ...lineItem,
            sort_order: maxOrder + 1,
            calculation_source: lineItem.calculation_source || 'manual',
          })
          .select()
          .single();
        
        if (error) throw error;
        return { data, action: 'added' as const };
      }
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['estimate-line-items', estimateId] });
      queryClient.invalidateQueries({ queryKey: ['estimates'] });
      
      if (result.action === 'replaced') {
        toast.success("Product updated with new selection");
      } else {
        toast.success("Line item added");
      }
    },
    onError: (error: any) => {
      toast.error("Failed to add/update line item: " + error.message);
    },
  });

  return {
    lineItems,
    isLoading,
    addLineItem,
    addMultipleLineItems,
    updateLineItem,
    deleteLineItem,
    reorderLineItems,
    replaceOrAddLineItem,
  };
};
